import React, { useState, useMemo, useEffect } from 'react';
import PageWrapper from '../../../../layout/PageWrapper/PageWrapper';
import SubHeader, { SubHeaderLeft, SubHeaderRight } from '../../../../layout/SubHeader/SubHeader';
import Page from '../../../../layout/Page/Page';
import Breadcrumb from '../../../../components/bootstrap/Breadcrumb';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import TableCustom from '../../../../components/MyCustom/TableCustom';
import Dropdown, { DropdownToggle, DropdownMenu, DropdownItem } from '../../../../components/bootstrap/Dropdown';
import Chart from '../../../../components/extras/Chart';
import useDarkMode from '../../../../hooks/useDarkMode';
import { COLORS_DARK, COLORS_LIGHT } from '../../../../types/dashColors';
import Icon from '../../../../components/icon/Icon';
import Spinner from '../../../../components/bootstrap/Spinner';
import EstablishmentDB from '../../../../database/wrappers/establishment';
import BillingChargesDB from '../../../../database/wrappers/billingCharges';
import Money from '../../../../helpers/utils/money/money';
import showNotification from '../../../../components/extras/showNotification';

const months = [
  'Jan', 'Fev', 'Mar', 'Abr', 'mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const MatrizSubscription = () => {
  const { darkModeStatus } = useDarkMode();
  const themeColors = darkModeStatus ? COLORS_DARK : COLORS_LIGHT;

  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(months[currentMonthIndex]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [chartSeries, setChartSeries] = useState([]);
  const [totals, setTotals] = useState({
    arrecadado: 0,
    pendente: 0,
    emitido: 0,
    chamados: 0
  });

  const monthIndex = useMemo(() => months.indexOf(selectedMonth), [selectedMonth]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const establishmentDB = new EstablishmentDB();
        const establishments = await establishmentDB.getAll();

        const billingDB = new BillingChargesDB();
        const allCharges = await billingDB.getAll();

        const yearCharges = allCharges.filter(charge => {
          if (!charge.created_at) return false;
          return charge.created_at.toDate().getFullYear() === selectedYear;
        });

        const currentMonthCharges = yearCharges.filter(charge => {
          const date = charge.created_at.toDate();
          return date.getMonth() === monthIndex;
        });

        const newRows = establishments.map(estab => {
          const estabCharges = currentMonthCharges.filter(c => c.estabId === estab.id);

          let totalPagar = 0;
          let totalPago = 0;
          let lojasPagantes = 0;
          let lojasPendentes = 0;
          let chamados = 0;
          const storesStatus = {};

          estabCharges.forEach(charge => {
            const val = charge.totalToPay || 0;
            totalPagar += val;

            if (charge.status === 'PAID') {
              totalPago += val;
              storesStatus[charge.storeId] = 'PAID';
            } else if (charge.status === 'PENDING') {
              if (storesStatus[charge.storeId] !== 'PAID') {
                storesStatus[charge.storeId] = 'PENDING';
              }
            }

            if (charge.report && charge.report.status === 'OPEN') {
              chamados++;
            }
          });

          Object.values(storesStatus).forEach(status => {
            if (status === 'PAID') lojasPagantes++;
            if (status === 'PENDING') lojasPendentes++;
          });

          let vencimento = '--';
          const chargeWithDate = estabCharges.find(c => c.chargeDate);
          if (chargeWithDate && chargeWithDate.chargeDate) {
            if (chargeWithDate.chargeDate.toDate) {
              vencimento = chargeWithDate.chargeDate.toDate().toLocaleDateString('pt-BR');
            } else {
              const d = new Date(chargeWithDate.chargeDate);
              if (!isNaN(d.getTime())) {
                vencimento = d.toLocaleDateString('pt-BR');
              }
            }
          }

          return {
            id: estab.id,
            data: `${selectedMonth}. ${selectedYear}`,
            estabelecimento: estab.name,
            vencimento: vencimento,
            pagamento: totalPago > 0 ? '--' : '--',
            lojasPagantes: lojasPagantes,
            lojasPendentes: lojasPendentes,
            chamados: chamados,
            totalPagar: Money.centsToMaskMoney(totalPagar),
            totalPago: Money.centsToMaskMoney(totalPago),
            original: { totalPagarVal: totalPagar, totalPagoVal: totalPago, chamadosVal: chamados }
          };
        }).filter(row => row.original.totalPagarVal > 0 || row.original.totalPagoVal > 0 || row.original.chamadosVal > 0);

        setRows(newRows);

        const newTotals = newRows.reduce((acc, row) => {
          acc.arrecadado += row.original.totalPagoVal;
          acc.pendente += (row.original.totalPagarVal - row.original.totalPagoVal);
          acc.emitido += row.original.totalPagarVal;
          acc.chamados += row.original.chamadosVal;
          return acc;
        }, { arrecadado: 0, pendente: 0, emitido: 0, chamados: 0 });

        setTotals(newTotals);

        const establishmentSeriesData = {};
        const establishmentTotalRevenue = {};

        yearCharges.forEach(charge => {
          const estabId = charge.estabId;
          const estabName = establishments.find(e => e.id === estabId)?.name || 'Desconhecido';

          if (!establishmentSeriesData[estabName]) {
            establishmentSeriesData[estabName] = new Array(12).fill(0);
            establishmentTotalRevenue[estabName] = 0;
          }

          const val = (charge.totalToPay / 100 || 0);
          const month = charge.created_at.toDate().getMonth();

          establishmentSeriesData[estabName][month] += val;
          establishmentTotalRevenue[estabName] += val;
        });

        const sortedEstablishments = Object.keys(establishmentTotalRevenue).sort((a, b) => {
          return establishmentTotalRevenue[b] - establishmentTotalRevenue[a];
        }).slice(0, 10);

        const newSeries = sortedEstablishments.map(name => ({
          name,
          data: establishmentSeriesData[name]
        }));

        setChartSeries(newSeries);

      } catch (error) {
        showNotification('Erro', 'Erro ao carregar dados da matriz', 'danger');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear, selectedMonth, monthIndex]);

  const columns = useMemo(() => [
    { label: 'DATA', field: 'data' },
    { label: 'ESTABELECIMENTO', field: 'estabelecimento' },
    { label: 'VENCIMENTO', field: 'vencimento' },
    { label: 'PAGAMENTO', field: 'pagamento' },
    { label: 'LOJAS PAGANTES', field: 'lojasPagantes' },
    { label: 'LOJAS PENDENTES', field: 'lojasPendentes' },
    { label: 'CHAMADOS', field: 'chamados' },
    { label: 'TOTAL A PAGAR', field: 'totalPagar' },
    { label: 'TOTAL PAGO', field: 'totalPago' },
  ], []);

  const chartOptions = {
    chart: {
      type: 'line',
      height: 350,
      toolbar: { show: false },
    },
    stroke: {
      curve: 'smooth',
      width: 3,
    },
    colors: ['#8b5cf6', '#fbbf24', '#22d3ee', '#10b981', '#f43f5e'],
    xaxis: {
      categories: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
      title: { text: 'Tempo', style: { fontSize: '14px', fontWeight: 'bold', color: themeColors.FONT } },
      labels: { style: { colors: themeColors.FONT } }
    },
    yaxis: {
      title: { text: 'Valor', style: { fontSize: '14px', fontWeight: 'bold', color: themeColors.FONT } },
      labels: {
        style: { colors: themeColors.FONT },
        formatter: (val) => Money.maskMoney(val),
      },
    },
    tooltip: {
      y: {
        formatter: (val) => Money.maskMoney(val),
      },
    },
    legend: {
      position: 'bottom',
      labels: { colors: themeColors.FONT },
      title: {
        text: 'Top 10 Estabelecimentos',
        style: {
          color: themeColors.FONT,
          fontSize: '14px',
          fontWeight: 'bold'
        }
      }
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    }
  };

  const TotalCard = ({ title, value, subValue, color }) => (
    <div className={`col-md-3`}>
      <div className={`p-3 rounded-3`} style={{ backgroundColor: color }}>
        <div className="fw-bold mb-1" style={{ fontSize: '0.9rem', color: '#333' }}>{title}</div>
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bolder" style={{ fontSize: '1.2rem', color: '#000' }}>{value}</span>
          {subValue && <span className="fw-bold" style={{ fontSize: '1.2rem', color: '#000' }}>- {subValue}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <PageWrapper title="Assinatura por matriz">
      <SubHeader>
        <SubHeaderLeft>
          <Breadcrumb
            list={[
              { title: 'Assinatura', to: '/subscription/matriz' },
              { title: 'Matriz', to: '/subscription/matriz' },
            ]}
          />
        </SubHeaderLeft>
        <SubHeaderRight>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <DropdownToggle>
                <div className="bg-white rounded-3 px-3 py-1 border d-flex align-items-center" style={{ height: '40px', cursor: 'pointer' }}>
                  <span className="fw-bold text-black">{selectedYear}</span>
                </div>
              </DropdownToggle>
              <DropdownMenu>
                {Array.from({ length: Math.max(0, currentYear - 2025 + 1) }, (_, i) => 2025 + i).map(year => (
                  <DropdownItem key={year} onClick={() => setSelectedYear(year)}>
                    {String(year)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            <div className="d-flex">
              {months.map((month) => (
                <Button
                  key={month}
                  color={selectedMonth === month ? 'warning' : 'light'}
                  className={`rounded-3 mx-1 border-0 ${selectedMonth === month ? 'fw-bold' : 'text-muted'}`}
                  size="sm"
                  onClick={() => setSelectedMonth(month)}
                  style={{ minWidth: '40px' }}
                >
                  {month}
                </Button>
              ))}
            </div>
          </div>
        </SubHeaderRight>
      </SubHeader>

      <Page container='fluid'>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <Spinner color="primary" />
          </div>
        ) : (
          <>
            {/* Totals Section */}
            <Card className="mb-4">
              <CardHeader>
                <CardLabel>
                  <div className="d-flex align-items-center justify-content-center">
                    <Icon icon="Circle" iconColor="dark" />
                    <CardTitle className="ms-2">Faturamento</CardTitle>
                  </div>
                </CardLabel>
              </CardHeader>
              <CardBody>
                <div className="row g-3">
                  <TotalCard
                    title="Valor arrecadado"
                    value={Money.centsToMaskMoney(totals.arrecadado)}
                    color="#d1fae5"
                  />
                  <TotalCard
                    title="Valor pendente"
                    value={Money.centsToMaskMoney(totals.pendente)}
                    color="#fee2e2"
                  />
                  <TotalCard
                    title="Valor emitido"
                    value={Money.centsToMaskMoney(totals.emitido)}
                    color="#fee2e2"
                  />
                  <TotalCard
                    title="Chamados"
                    value={totals.chamados}
                    color="#eecaca"
                  />
                </div>
              </CardBody>
            </Card>

            {/* Table Section */}
            <Card className="mb-5">
              <CardBody>
                <TableCustom
                  title="Assinatura por matriz"
                  columns={columns}
                  rows={rows}
                  fileName={'AssinaturaMatriz'}
                />
              </CardBody>
            </Card>

            {/* Chart Section */}
            <Card>
              <h3 className="mb-4 p-4 pb-0">Faturamento por estabelecimento</h3>
              <CardBody>
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="line"
                  height={350}
                />
              </CardBody>
            </Card>
          </>
        )}
      </Page>
    </PageWrapper>
  );
};

export default MatrizSubscription;
